import { forwardRef, useImperativeHandle, useRef } from 'react';
import useModalState from '@sdk/components/ui/modal/useModalState';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Modal from '@sdk/components/ui/modal/Modal';
import ToastComponent from '@sdk/common/toast/ToastComponent';
import { SUBSCRIPTION_NAMES, SUBSCRIPTION_LEVELS } from '@sdk/account/constants';
import { SubscriptionPlanNames } from '@sdk/account/types';
import Text14 from '@sdk/components/ui/typography/Text14';
import Column from '@sdk/components/ui/spacing/Column';
import PickerField from '@sdk/components/forms/PickerField';
import ModalFooterActionButtons from '@sdk/components/ui/modal/ModalFooterActionButtons';
import ModalBodyWithHeader from '@sdk/components/ui/modal/ModalBodyWithHeader';
import ModalErrorDisplay from '@sdk/components/ui/modal/components/modalErrorDisplay/ModalErrorDisplay';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';

import DashboardBluePremiumSvg from '@web/assets/svg/Icons/32/dashboard-blue.svg';
import DashboardBlueBusinessSvg from '@web/assets/svg/Icons/32/dashboard-blue-business.svg';
import FeaturesToLose from '@web/account/billing/FeaturesToLose/FeaturesToLose';
import CreditCardModal from '@web/account/billing/CreditCardModal/CreditCardModal';
import DowngradeReason, {
  DowngradeMode,
} from '@web/account/billing/DowngradeReason/DowngradeReason';
import Grant50PercentOffModal from '@web/account/billing/Grant50PercentOffModal/Grant50PercentOffModal';
import DowngradeOfferModal from '@web/account/billing/DowngradeOfferModal/DowngradeOfferModal';
import PauseSubscriptionModal from '@web/account/billing/PauseSubscriptionModal/PauseSubscriptionModal';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';
import type { SubscriptionUtilsType } from '@web/account/types';
import { WithStripeManualPaymentMethod } from '@web/account/components/WithStripeManualPaymentMethod';

import useDowngradeSubscription from './downgradeSubscriptionModal.hooks';
import { getSubmitButtonText } from './downgradeSubscriptionModal.utils';

type Props = {
  subscriptionUtils: SubscriptionUtilsType;
  orgSettings: OrgSettings;
  currency: CurrencyType;
};

type ForwardedAttrs = {
  open: () => void;
};

const DowngradeSubscriptionModal = forwardRef<ForwardedAttrs, Props>(
  ({ subscriptionUtils, orgSettings, currency }, ref) => {
    const { isVisible, open, close } = useModalState();
    const config = useRef({ noDismiss: true }).current;
    const { billingInfo } = useBillingInfo();

    const {
      showFeaturesToLose,
      setShowFeaturesToLose,
      isDowngradeOnly,
      setIsDowngradeOnly,
      selectedReasonId,
      setSelectedReasonId,
      selectedReasonText,
      setSelectedReasonText,
      setReasonRequiresText,
      grant50PercentModalState,
      downgradeOfferModalState,
      pauseSubscriptionModalState,
      creditCardModalState,
      onSubmit,
      isFormValid,
      isSubmitted,
      setIsSubmitted,
      setIs50PercentDismissed,
      setIsPauseOfferDismissed,
    } = useDowngradeSubscription(close);

    const submitButtonText = getSubmitButtonText(
      showFeaturesToLose,
      isDowngradeOnly,
      billingInfo.subscriptionLevel,
      orgSettings.isOldPremium
    );

    useImperativeHandle(ref, () => ({
      open({ downgradeOnly = false } = {}) {
        setShowFeaturesToLose(true);
        setSelectedReasonId(null);
        setSelectedReasonText('');
        setIsSubmitted(false);
        setIsDowngradeOnly(downgradeOnly);
        setIs50PercentDismissed(false);
        setIsPauseOfferDismissed(false);
        open();
      },
    }));

    return (
      <>
        <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
          <ModalBodyWithHeader
            width={620}
            titleThemeColor={ThemeColorType.primary}
            titleText={
              isDowngradeOnly
                ? gettext('Downgrade to Premium')
                : gettext('Cancel %(subscriptionName)s', {
                    subscriptionName: orgSettings.isOldPremium
                      ? SubscriptionPlanNames.premium
                      : SUBSCRIPTION_NAMES[billingInfo.subscriptionLevel],
                  })
            }
            titleIcon={
              isDowngradeOnly || billingInfo.subscriptionLevel === 1 ? (
                <DashboardBluePremiumSvg />
              ) : (
                <DashboardBlueBusinessSvg />
              )
            }
            close={close}>
            {showFeaturesToLose ? (
              <FeaturesToLose
                subscriptionLevel={billingInfo.subscriptionLevel}
                isDowngradeOnly={isDowngradeOnly}
                isOldPremium={orgSettings.isOldPremium}
              />
            ) : (
              <>
                <DowngradeReason
                  selectedReasonId={selectedReasonId}
                  setSelectedReasonId={setSelectedReasonId}
                  selectedReasonText={selectedReasonText}
                  setSelectedReasonText={setSelectedReasonText}
                  setReasonRequiresText={setReasonRequiresText}
                  mode={isDowngradeOnly ? DowngradeMode.downgrade : DowngradeMode.cancel}
                  isBusiness={billingInfo.subscriptionLevel === SUBSCRIPTION_LEVELS.BUSINESS}
                  showErrors={isSubmitted}
                />
              </>
            )}
            <ModalFooterActionButtons
              cancel={close}
              isSubmitAsync={!showFeaturesToLose}
              isSubmitActive={isFormValid}
              submit={onSubmit}
              cancelText={gettext('Nevermind')}
              submitText={submitButtonText}
              allowSubmitCallbackIfInactive={true}
            />
          </ModalBodyWithHeader>
          {isSubmitted && !isFormValid && (
            <ModalErrorDisplay error={gettext('Please specify...')}></ModalErrorDisplay>
          )}
        </Modal>
        <ToastComponent />
        <Grant50PercentOffModal {...grant50PercentModalState} />
        <DowngradeOfferModal
          subscriptionUtils={subscriptionUtils}
          orgSettings={orgSettings}
          currency={currency}
          reasonId={selectedReasonId}
          reasonText={selectedReasonText}
          {...downgradeOfferModalState}
        />
        <PauseSubscriptionModal {...pauseSubscriptionModalState} fromCancel />
        <WithStripeManualPaymentMethod>
          <CreditCardModal {...creditCardModalState} />
        </WithStripeManualPaymentMethod>
      </>
    );
  }
);

DowngradeSubscriptionModal.displayName = 'DowngradeSubscriptionModal';

export default DowngradeSubscriptionModal;
